import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import {
  getJob, completeStep, retryStep, unblockJob, pauseJob, getStepEvidence,
  STEP_LABELS, type PublishStepRow, rebuildJob,
} from '@/api/playPublish';

interface Props {
  jobId: string | null;
  onClose: () => void;
}

const FINISHED = new Set(['done', 'skipped']);

// Mirrors nextRunnable() on the worker: the first non-finished step, and only if
// it is still pending. Kept in sync deliberately — the server is the authority
// and will reject an out-of-sequence completion regardless.
function activeStep(steps: PublishStepRow[]): PublishStepRow | null {
  for (const s of [...steps].sort((a, b) => a.ord - b.ord)) {
    if (FINISHED.has(s.status)) continue;
    return s.status === 'pending' ? s : null;
  }
  return null;
}

const STATUS_CLS: Record<string, string> = {
  done: 'bg-green-500/15 text-green-400',
  skipped: 'bg-surface-muted text-content-muted',
  running: 'bg-blue-500/15 text-blue-400',
  failed: 'bg-red-500/15 text-red-400',
  pending: 'bg-surface-muted text-content-muted',
};

// A screenshot the runner took when it gave up on a step: enough to tell a
// selector problem from "Google is asking for a passkey" without opening the
// profile. Loaded on demand — these are console screenshots of a live account.
function StepEvidence({ jobId, step }: { jobId: string; step: string }) {
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState('');
  const query = useQuery({
    queryKey: ['publish-evidence', jobId, step],
    queryFn: () => getStepEvidence(jobId, step),
    enabled: open,
    staleTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (!query.data) return;
    const next = URL.createObjectURL(query.data);
    setSrc(next);
    return () => URL.revokeObjectURL(next);
  }, [query.data]);

  if (!open) {
    return (
      <button type="button" className="text-[11px] text-sky-400 hover:underline mt-1" onClick={() => setOpen(true)}>
        Screenshot
      </button>
    );
  }
  if (query.isError) return <div className="text-[11px] text-red-400 mt-1">{(query.error as Error).message}</div>;
  if (!src) return <div className="h-24 mt-1 animate-pulse bg-surface-muted rounded" />;
  return <img src={src} alt={`${step} screenshot`} className="mt-1 max-h-64 rounded border border-border" />;
}

export function PublishJobDrawer({ jobId, onClose }: Props) {
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['publish-job', jobId],
    queryFn: () => getJob(jobId!),
    enabled: !!jobId,
    // API steps are picked up by the every-minute cron and ui steps by the
    // runner, so a job in flight changes without any action here.
    refetchInterval: (q) => {
      const s = (q.state.data as any)?.job?.status;
      return s === 'pending' || s === 'running' ? 10_000 : false;
    },
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['publish-job', jobId] });
    qc.invalidateQueries({ queryKey: ['publish-jobs'] });
  };

  const doneMut = useMutation({
    mutationFn: (step: string) => completeStep(jobId!, step),
    onSuccess: invalidate,
  });
  const retryMut = useMutation({
    mutationFn: (step: string) => retryStep(jobId!, step),
    onSuccess: invalidate,
  });
  const unblockMut = useMutation({
    mutationFn: () => unblockJob(jobId!),
    onSuccess: invalidate,
  });
  const pauseMut = useMutation({
    mutationFn: () => pauseJob(jobId!),
    onSuccess: invalidate,
  });
  const rebuildMut = useMutation({
    mutationFn: () => rebuildJob(jobId!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['publish-job', jobId] }); qc.invalidateQueries({ queryKey: ['publish-jobs'] }); },
  });

  // The server refuses some actions with a reason worth reading (e.g. closing
  // create_app for a package another account already registered).
  const actionError = doneMut.error || retryMut.error || unblockMut.error || pauseMut.error || rebuildMut.error;

  const job = data?.job;
  const steps = data?.steps ?? [];
  const active = activeStep(steps);

  return (
    <Modal
      isOpen={!!jobId}
      onClose={onClose}
      title={job ? `${job.title} — ${job.package_name}` : 'Publication'}
      size="3xl"
    >
      {isLoading && <p className="text-sm text-content-muted">Loading…</p>}
      {error && <p className="text-sm text-red-400">{(error as Error).message}</p>}
      {actionError && (
        <div className="mb-4 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-sm text-red-400">
          {(actionError as Error).message}
        </div>
      )}

      {job && (
        <>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mb-4 text-sm">
            <span className="text-content-muted">Account: <span className="text-content">{job.account_name}</span></span>
            <span className="text-content-muted">Template: <span className="text-content">{job.template_name}</span></span>
            <span className="text-content-muted">Track: <span className="text-content">{job.track}</span></span>
            <span className="text-content-muted">Status: <span className="text-content">{job.status}</span></span>
          </div>

          {(job.status === 'pending' || job.status === 'running') && (
            <div className="mb-4 flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-border">
              <span className="text-xs text-content-muted">
                Need to change something in Play Console by hand? Stop the auto-fill first, then resume it.
              </span>
              <Button
                size="sm" variant="secondary" loading={pauseMut.isPending}
                onClick={() => {
                  if (window.confirm('Stop the auto-fill? A step the runner is in the middle of is dropped and redone on Resume.')) pauseMut.mutate();
                }}
              >
                Stop
              </Button>
            </div>
          )}

          {job.status === 'blocked' && job.blocked_reason === 'paused' && (
            <div className="mb-4 px-3 py-2 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-center justify-between gap-3">
              <span className="text-sm text-amber-400">
                Auto-fill stopped
                <span className="block text-xs text-content-muted">
                  Make your changes in Play Console (Live browser on this account works now). Finished a step yourself? Press Done in Console on it. Then Resume to carry on from the first unfinished step.
                </span>
              </span>
              <Button size="sm" loading={unblockMut.isPending} onClick={() => unblockMut.mutate()}>
                Resume
              </Button>
            </div>
          )}

          {job.status === 'blocked' && job.blocked_reason !== 'paused' && (
            <div className="mb-4 px-3 py-2 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-center justify-between">
              <span className="text-sm text-amber-400">
                Waiting for a human — {job.blocked_reason ?? 'manual'}
                {active?.kind === 'ui' && <span className="block text-xs text-content-muted">Did it yourself in the console? Press Done in Console on the step below.</span>}
              </span>
              <Button size="sm" variant="secondary" loading={unblockMut.isPending} onClick={() => unblockMut.mutate()}>
                Unblock
              </Button>
            </div>
          )}

          {job.error && (
            <div className="mb-4 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-sm text-red-400 flex items-center justify-between gap-3">
              <span>{job.error}</span>
              {/build/i.test(job.error) && (
                <Button size="sm" variant="secondary" loading={rebuildMut.isPending} onClick={() => rebuildMut.mutate()}
                  title="Fix the cause first (Apps Setup → Connect / Build), then start a fresh AAB for this job">
                  Build again
                </Button>
              )}
            </div>
          )}

          <div className="border border-border rounded-lg divide-y divide-border">
            {[...steps].sort((a, b) => a.ord - b.ord).map((s) => {
              const isActive = active?.step === s.step;
              return (
                <div
                  key={s.step}
                  className={`px-3 py-2.5 flex items-start gap-3 ${isActive ? 'bg-surface-muted/50' : ''}`}
                >
                  <span className="text-xs text-content-muted w-5 pt-0.5">{s.ord + 1}</span>

                  <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium mt-0.5 ${
                    s.kind === 'ui' ? 'bg-purple-500/15 text-purple-400' : 'bg-sky-500/15 text-sky-400'
                  }`}>
                    {s.kind.toUpperCase()}
                  </span>

                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-content">{STEP_LABELS[s.step] ?? s.step}</div>
                    {s.error && <div className="text-xs text-red-400 mt-0.5">{s.error}</div>}
                    {s.result_json && FINISHED.has(s.status) && (
                      <pre className="text-[11px] text-content-muted font-mono mt-1 whitespace-pre-wrap break-all">
                        {s.result_json}
                      </pre>
                    )}
                    {s.attempts > 1 && (
                      <div className="text-[11px] text-content-muted mt-0.5">{s.attempts} attempts</div>
                    )}
                    {s.status === 'running' && s.runner_id && (
                      <div className="text-[11px] text-content-muted mt-0.5">held by runner {s.runner_id}</div>
                    )}
                    {s.evidence_key && <StepEvidence jobId={jobId!} step={s.step} />}
                  </div>

                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                    STATUS_CLS[s.status] ?? STATUS_CLS.pending
                  }`}>
                    {s.status}
                  </span>

                  <span className="w-32 flex justify-end">
                    {/* A ui step the runner holds is not ours to close by hand —
                        the server rejects it anyway, since it is no longer pending.
                        On a blocked job this is the way out: the step the runner
                        gave up on was done in the console, and the block goes too. */}
                    {isActive && s.kind === 'ui' && (
                      <Button
                        size="sm" loading={doneMut.isPending} onClick={() => doneMut.mutate(s.step)}
                        title={job.status === 'blocked'
                          ? (job.blocked_reason === 'paused' ? 'Done by hand in Play Console: closes this step; the job stays stopped until Resume' : 'Done by hand in Play Console: closes this step and lifts the block')
                          : undefined}
                      >
                        Done in Console
                      </Button>
                    )}
                    {s.status === 'failed' && (
                      <Button size="sm" variant="secondary" loading={retryMut.isPending} onClick={() => retryMut.mutate(s.step)}>
                        Retry
                      </Button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-content-muted mt-3">
            UI steps run in the Play Console — the ShardX runner claims them when it is up, and a
            human can close them here otherwise. API steps run on the every-minute cron. The first
            four steps register the package name, which is what unlocks the API for the rest.
          </p>
        </>
      )}
    </Modal>
  );
}
